import { useState, useEffect, useContext } from 'react';
import {
    IconButton,
    Badge,
    Menu,
    MenuItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Typography,
    Box,
    Button,
    Tooltip,
    Chip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import TaskIcon from '@mui/icons-material/Task';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { useSelector } from 'react-redux';
import notificationService from '../../services/NotificationService';

const NotificationBadge = () => {
    const userEmail = useSelector(state => state.UserMetaData.email);
    const userUid = useSelector(state => state.UserMetaData.uid);
    const language = useSelector(state => state.App_Settings.language);
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [pendingActions, setPendingActions] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        if (userEmail && userUid) {
            // Start polling for notifications
            notificationService.startPolling(userEmail, userUid, handleNotificationUpdate);
        }

        return () => {
            // Stop polling when component unmounts
            notificationService.stopPolling();
        };
    }, [userEmail, userUid]);

    const handleNotificationUpdate = (data) => {
        setNotifications(data.notifications || []);
        setPendingActions(data.pendingActions || []);
        setTotalCount((data.notifications?.length || 0) + (data.pendingActions?.length || 0));
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMarkAsRead = async (notificationDate) => {
        await notificationService.markAsRead(notificationDate);
    };

    const handleClearAll = async () => {
        if (window.confirm('Clear all notifications?')) {
            await notificationService.clearAll();
        }
    };

    const handleNotificationClick = (notification) => {
        // Mark as read
        handleMarkAsRead(notification.date);
        handleClose();
        
        // Log notification details for debugging
        console.log('[NOTIFICATION] Clicked notification:', notification);
    };

    const handlePendingActionClick = (action) => {
        handleClose();
        // Navigate to change requests page using app context
        // This would need a ChangeRequests page to be added to your app
        console.log('[NOTIFICATION] Pending action clicked:', action);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'change_request':
                return <TaskIcon color="primary" />;
            case 'change_request_approved':
                return <CheckCircleIcon color="success" />;
            case 'change_request_rejected':
                return <CancelIcon color="error" />;
            default:
                return <InfoIcon color="info" />;
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp * 1000);
        const now = new Date();
        const diff = now - date;
        
        // Less than 1 minute
        if (diff < 60000) {
            return language === 'ar' ? 'الآن' : 'Just now';
        }
        // Less than 1 hour
        if (diff < 3600000) {
            const minutes = Math.floor(diff / 60000);
            return language === 'ar' ? `منذ ${minutes} دقيقة` : `${minutes}m ago`;
        }
        // Less than 24 hours
        if (diff < 86400000) {
            const hours = Math.floor(diff / 3600000);
            return language === 'ar' ? `منذ ${hours} ساعة` : `${hours}h ago`;
        }
        // More than 24 hours
        const days = Math.floor(diff / 86400000);
        return language === 'ar' ? `منذ ${days} يوم` : `${days}d ago`;
    };

    const TEXT = {
        Notifications: { en: 'Notifications', ar: 'الإشعارات' },
        PendingActions: { en: 'Pending Actions', ar: 'الإجراءات المعلقة' },
        NoNotifications: { en: 'No new notifications', ar: 'لا توجد إشعارات جديدة' },
        NoPendingActions: { en: 'No pending actions', ar: 'لا توجد إجراءات معلقة' },
        ClearAll: { en: 'Clear All', ar: 'مسح الكل' },
        ViewDetails: { en: 'View Details', ar: 'عرض التفاصيل' },
        MarkAsRead: { en: 'Mark as read', ar: 'وضع علامة كمقروء' }
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <Tooltip title={TEXT.Notifications[language] || TEXT.Notifications.en}>
                <IconButton
                    color="inherit"
                    onClick={handleClick}
                    size="large"
                >
                    <Badge badgeContent={totalCount} color="error">
                        {totalCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        maxHeight: 480,
                        width: 380,
                        maxWidth: '90vw'
                    }
                }}
            >
                <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={600}>
                        {TEXT.Notifications[language] || TEXT.Notifications.en}
                    </Typography>
                    {(notifications.length > 0 || pendingActions.length > 0) && (
                        <Button
                            size="small"
                            startIcon={<ClearAllIcon />}
                            onClick={handleClearAll}
                        >
                            {TEXT.ClearAll[language] || TEXT.ClearAll.en}
                        </Button>
                    )}
                </Box>
                <Divider />

                {/* Pending Actions */}
                {pendingActions.length > 0 && (
                    <>
                        <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                {TEXT.PendingActions[language] || TEXT.PendingActions.en} ({pendingActions.length})
                            </Typography>
                        </Box>
                        {pendingActions.map((action, index) => (
                            <MenuItem
                                key={`pending-${index}`}
                                onClick={() => handlePendingActionClick(action)}
                                sx={{
                                    borderLeft: 3,
                                    borderColor: 'warning.main',
                                    py: 1.5
                                }}
                            >
                                <ListItemIcon>
                                    {getNotificationIcon(action.payload.type)}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                {action.context.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {action.context.message}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Box display="flex" gap={1} mt={0.5}>
                                            <Chip
                                                label={formatTimestamp(action.timestamp)}
                                                size="small"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                            <Chip
                                                label={action.context.priority || 'normal'}
                                                size="small"
                                                color={action.context.priority === 'high' ? 'error' : 'default'}
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                        </Box>
                                    }
                                />
                            </MenuItem>
                        ))}
                        <Divider />
                    </>
                )}

                {/* Regular Notifications */}
                {notifications.length > 0 ? (
                    notifications.map((notification, index) => (
                        <MenuItem
                            key={`notif-${index}`}
                            onClick={() => handleNotificationClick(notification)}
                            sx={{ py: 1.5 }}
                        >
                            <ListItemIcon>
                                {getNotificationIcon(notification.payload.type)}
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                            {notification.context.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {notification.context.message}
                                        </Typography>
                                    </Box>
                                }
                                secondary={
                                    <Typography variant="caption" color="text.secondary">
                                        {formatTimestamp(notification.timestamp)}
                                    </Typography>
                                }
                            />
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsRead(notification.date);
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </MenuItem>
                    ))
                ) : pendingActions.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            {TEXT.NoNotifications[language] || TEXT.NoNotifications.en}
                        </Typography>
                    </Box>
                ) : null}
            </Menu>
        </>
    );
};

export default NotificationBadge;
